import { type CSSProperties, type ReactNode, useEffect, useRef } from 'react';

interface ClickAwayListenerProps {
  children: ReactNode | ReactNode[];
  className?: string;
  style?: CSSProperties;
  onClickAway: () => void;
}

const ClickAwayListener = ({ children, className, onClickAway, style }: ClickAwayListenerProps) => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const hasInteractedRef = useRef(false);

  useEffect(() => {
    const element = targetRef.current;
    if (!element) {
      return undefined;
    }

    const markInteracted = (event: Event) => {
      if (event.target instanceof Node && element.contains(event.target)) {
        hasInteractedRef.current = true;
      }
    };

    const handleClickAway = (event: PointerEvent) => {
      if (!hasInteractedRef.current) {
        return;
      }

      if (event.target instanceof Node && !element.contains(event.target)) {
        onClickAway();
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!hasInteractedRef.current) {
        return;
      }

      const target = event.target as HTMLElement;
      if (!element.contains(target)) {
        onClickAway();
      }
    };

    element.addEventListener('pointerdown', markInteracted);
    element.addEventListener('focusin', markInteracted);

    document.addEventListener('pointerdown', handleClickAway);
    document.addEventListener('focusin', handleFocusIn);

    return () => {
      element.removeEventListener('pointerdown', markInteracted);
      element.removeEventListener('focusin', markInteracted);

      document.removeEventListener('pointerdown', handleClickAway);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [onClickAway]);

  return (
    <div className={className} ref={targetRef} style={style}>
      {children}
    </div>
  );
};

export default ClickAwayListener;
